import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../web/components-v2/Button';
import { THEME } from '../../../web/components-v2/Theme/theme';
import {
	CALL_TYPES,
	DISQUALIFIER_META,
	NEGATIVE_MODIFIERS,
	NEW_WORK_CALL_TYPES,
	OUTCOME_TIERS,
	PROCESS_AREAS,
	SUB_PRODUCT_PITCH_CAP,
	SUB_PRODUCT_PITCH_FLAG,
} from '../../components/CallRecordingDetails/components/coachingRubric';
import { styleSheet } from './adherenceDrawer.styles';

const { colors } = THEME;

const formatMultiplier = (m: number) => String(Math.round(m * 1000) / 1000);

const clampPct = (n: number) => Math.max(0, Math.min(100, n));

const areaBarColor = (pct: number) =>
	pct >= 85 ? colors.success.main : pct >= 50 ? colors.primary.main : colors.warning.main;

const scoreColor = (score: number, tierA: boolean) => {
	if (tierA) return colors.danger.main;
	if (score >= 80) return colors.success.dark;
	if (score >= 50) return colors.primary.dark;
	return colors.text.main;
};

const confidenceInfo = (c: number) => {
	const pct = Math.round(c * 100);
	const label = c >= 0.85 ? 'High' : c >= 0.6 ? 'Medium' : 'Low';
	const text = c >= 0.85 ? colors.success.dark : c >= 0.6 ? colors.warning.dark : colors.danger.main;
	const bar = c >= 0.85 ? colors.success.main : c >= 0.6 ? colors.warning.main : colors.danger.main;
	return { pct, label: `${label} · ${pct}%`, text, bar };
};

const STEP_LABELS: Partial<Record<Api.SalesCallStep, string>> = {
	...Object.fromEntries(PROCESS_AREAS.map(a => [a.step, a.label])),
	Other: 'Pacing / tone',
	Unknown: 'Unclassified',
};

const stepLabel = (step: Api.SalesCallStep) => STEP_LABELS[step] ?? step;

const CLASSIFICATION_META: Record<Api.CoachingClassification, { label: string; tone: 'good' | 'missed' | 'neutral' }> =
	{
		followed: { label: 'Followed', tone: 'good' },
		partially_followed: { label: 'Partly followed', tone: 'neutral' },
		not_followed: { label: 'Not followed', tone: 'missed' },
		not_applicable: { label: 'No chance to act', tone: 'neutral' },
	};

const QUALITY_META: Record<Api.CoachingSuggestionQuality, { label: string; tone: 'good' | 'missed' | 'neutral' }> = {
	Sound: { label: 'Sound advice', tone: 'good' },
	Mistimed: { label: 'Mistimed', tone: 'neutral' },
	Irrelevant: { label: 'Irrelevant', tone: 'neutral' },
	Unknown: { label: 'Not judged', tone: 'neutral' },
};

const GAME_PLAN_META: Record<
	Api.GamePlanPitchAdherence,
	{ label: string; sub: string; tone: 'good' | 'missed' | 'neutral' }
> = {
	Delivered: { label: 'Delivered', sub: 'The rep used the pitch the game plan recommended', tone: 'good' },
	PartiallyDelivered: { label: 'Partly delivered', sub: 'Some of the recommended pitch landed', tone: 'neutral' },
	Ignored: { label: 'Not used', sub: 'The plan recommended a pitch and the rep did not deliver it', tone: 'missed' },
	NotApplicable: {
		label: 'Not applicable',
		sub: 'No game plan on this thread, or it recommended no pitch',
		tone: 'neutral',
	},
};

const toneStyle = (tone: 'good' | 'missed' | 'neutral') =>
	tone === 'good' ? styleSheet.tagGood : tone === 'missed' ? styleSheet.tagMissed : styleSheet.tagNeutral;

const CheckIcon = ({ color }: { color: string }) => (
	<svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke={color} strokeWidth='2.4' aria-hidden='true'>
		<path d='M20 6L9 17l-5-5' />
	</svg>
);

const CircleIcon = ({ color }: { color: string }) => (
	<svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke={color} strokeWidth='2' aria-hidden='true'>
		<circle cx='12' cy='12' r='9' />
	</svg>
);

const NaIcon = () => (
	<svg
		width='14'
		height='14'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2'
		aria-hidden='true'
		style={{ flex: 'none' }}
	>
		<circle cx='12' cy='12' r='9' />
		<path d='M8 12h8' />
	</svg>
);

const CloseIcon = () => (
	<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' aria-hidden='true'>
		<path d='M6 6l12 12M18 6L6 18' />
	</svg>
);

interface IProps {
	result: Api.ICoachingAdherenceResult;
	onClose?: () => void;
}

/**
 * Full points-based rubric breakdown for a new-scorer coaching result (`rubricBreakdown` present).
 * Per-area scores are the wire's normalized 0–100 values (not raw points); per-trigger point weights
 * and call-type multipliers come from the local taxonomy — see `coachingRubric.ts`.
 */
export const RubricBreakdown: React.FC<IProps> = ({ result, onClose }) => {
	const breakdown = result.rubricBreakdown;
	if (!breakdown) return null;

	const firedTriggers = new Set(breakdown.firedTriggers ?? []);
	const naAreas = new Set<Api.SalesCallStep>(breakdown.naAreas ?? []);
	const flags = breakdown.flags ?? [];
	const negativeModifiers = breakdown.negativeModifiers ?? [];
	const disqualifiers = breakdown.disqualifiers ?? [];

	const score = result.score;
	const rawFinal = breakdown.rawFinalBeforeDisqualifiers;
	const showRaw = rawFinal !== score;
	const mult = formatMultiplier(breakdown.outcomeMultiplier);
	const callTypeBase = CALL_TYPES.find(c => c.type === breakdown.callType)?.multiplier ?? 1;
	const conf = confidenceInfo(breakdown.confidence ?? 0);
	const showNewWork = NEW_WORK_CALL_TYPES.has(breakdown.callType);
	const showPrevClientCap = negativeModifiers.includes('previous_client');

	const gamePlan = result.gamePlanPitch ?? null;
	const gamePlanMeta = GAME_PLAN_META[gamePlan?.adherence ?? 'NotApplicable'] ?? GAME_PLAN_META.NotApplicable;
	const suggestions = result.suggestionEvaluations ?? [];
	const showSuggestions =
		suggestions.length > 0 ||
		result.suggestionQualityScore != null ||
		result.suggestionAdherenceScore != null ||
		result.missedSoundSuggestions != null;

	return (
		<div className={css(styleSheet.root)}>
			<header className={css(styleSheet.header)}>
				<div className={css(styleSheet.headerTop)}>
					<div>
						<div className={css(styleSheet.eyebrow)}>Adherence breakdown</div>
						<div className={css(styleSheet.scoreRow)}>
							<span
								className={css(styleSheet.scoreValue)}
								style={{ color: scoreColor(score, breakdown.tierADisqualifier) }}
							>
								{score < 0 ? '—' : score}
							</span>
							<span className={css(styleSheet.scoreOutOf)}>/ 100</span>
						</div>
						{showRaw && (
							<div className={css(styleSheet.rawLine)}>
								Before disqualifiers: <span className={css(styleSheet.strike)}>{rawFinal}</span>
							</div>
						)}
					</div>
					{onClose && (
						<Button
							variant='ghost'
							size='icon'
							onClick={onClose}
							aria-label='Close adherence breakdown'
							styles={[styleSheet.closeButton]}
						>
							<CloseIcon />
						</Button>
					)}
				</div>

				<div className={css(styleSheet.badgeRow)}>
					{breakdown.isColdExemplar && (
						<span className={css(styleSheet.badge, styleSheet.badgeExemplar)}>★ Cold exemplar</span>
					)}
					{breakdown.tierADisqualifier && (
						<span className={css(styleSheet.badge, styleSheet.badgeDqZero)}>⛔ DQ = 0</span>
					)}
					<div className={css(styleSheet.confidenceWrap)}>
						<span className={css(styleSheet.confidenceLabel)}>Confidence</span>
						<div className={css(styleSheet.confidenceTrack)}>
							<div className={css(styleSheet.meterFill)} style={{ width: `${conf.pct}%`, backgroundColor: conf.bar }} />
						</div>
						<span className={css(styleSheet.confidenceValue)} style={{ color: conf.text }}>
							{conf.label}
						</span>
					</div>
				</div>

				<div className={css(styleSheet.breakdownLine)}>
					<span>
						process <b>{breakdown.processEarned}</b>/{breakdown.applicableProcessMax}
					</span>
					<span>
						outcome {breakdown.outcomeBase}×{mult}=<b>{breakdown.outcomePoints}</b>
					</span>
					<span>raw {rawFinal}</span>
				</div>
			</header>

			<div className={css(styleSheet.body)}>
				<div className={css(styleSheet.section)}>
					<div className={css(styleSheet.sectionLabel)}>Call type · base ×{formatMultiplier(callTypeBase)}</div>
					<div className={css(styleSheet.chipRow)}>
						{CALL_TYPES.map(ct => (
							<span
								key={ct.type}
								className={css(styleSheet.chip, ct.type === breakdown.callType && styleSheet.chipActive)}
							>
								{ct.label} ×{formatMultiplier(ct.multiplier)}
							</span>
						))}
					</div>
					{showNewWork && (
						<div className={css(styleSheet.note)}>
							New-work score <b>{formatMultiplier(breakdown.newWorkScore)}</b> → applied multiplier <b>{mult}</b>
						</div>
					)}
					{showPrevClientCap && (
						<div className={css(styleSheet.note, styleSheet.noteWarning)}>
							Previous client — multiplier capped at 0.25
						</div>
					)}
				</div>

				<div className={css(styleSheet.section)}>
					<div className={css(styleSheet.sectionLabel)}>
						Process — {breakdown.processEarned} / {breakdown.applicableProcessMax}
					</div>
					<div className={css(styleSheet.areaList)}>
						{PROCESS_AREAS.map(area => {
							const areaScore = result[area.scoreKey];
							const isNA = areaScore == null || naAreas.has(area.step);
							const subProductCapped = area.step === 'Pitch' && flags.includes(SUB_PRODUCT_PITCH_FLAG);
							const cap = subProductCapped ? SUB_PRODUCT_PITCH_CAP : area.cap;
							const pct = isNA ? 0 : clampPct(areaScore as number);
							return (
								<div key={area.step} className={css(styleSheet.areaCard, isNA && styleSheet.areaCardNA)}>
									<div className={css(styleSheet.areaHead)}>
										<span className={css(styleSheet.areaLabel)}>
											{area.label} <span style={{ color: colors.text.light, fontWeight: 400 }}>· {cap} pts</span>
										</span>
										<span className={css(isNA ? styleSheet.areaEarnedNA : styleSheet.areaEarned)}>
											{isNA ? 'N/A' : `${areaScore} / 100`}
										</span>
									</div>
									{isNA ? (
										<div className={css(styleSheet.naRow)}>
											<NaIcon />
											N/A — no opportunity (excluded from denominator)
										</div>
									) : (
										<>
											<div className={css(styleSheet.areaBarTrack)}>
												<div
													className={css(styleSheet.meterFill)}
													style={{ width: `${pct}%`, backgroundColor: areaBarColor(pct) }}
												/>
											</div>
											<div className={css(styleSheet.triggerList)}>
												{area.triggers.map(t => {
													const fired = firedTriggers.has(t.id);
													return (
														<div
															key={t.id}
															className={css(
																styleSheet.triggerRow,
																fired ? styleSheet.triggerRowFired : styleSheet.triggerRowIdle
															)}
														>
															<span className={css(styleSheet.triggerIcon)}>
																{fired ? (
																	<CheckIcon color={colors.success.dark} />
																) : (
																	<CircleIcon color={colors.text.light} />
																)}
															</span>
															<span
																className={css(styleSheet.triggerLabel)}
																style={{ color: fired ? colors.text.main : colors.text.light }}
															>
																{t.label}
															</span>
															<span
																className={css(styleSheet.triggerPoints)}
																style={{ color: fired ? colors.success.dark : colors.text.light }}
															>
																{fired ? `+${t.points}` : t.points}
															</span>
														</div>
													);
												})}
											</div>
											{subProductCapped && (
												<div className={css(styleSheet.capNote)}>
													⚑ Pitch area cap reduced to {SUB_PRODUCT_PITCH_CAP} (sub-product pitch only)
												</div>
											)}
										</>
									)}
								</div>
							);
						})}
					</div>
				</div>

				<div className={css(styleSheet.section)}>
					<div className={css(styleSheet.sectionLabel)}>Call outcome</div>
					<div className={css(styleSheet.outcomeMath)}>
						{breakdown.outcomeBase} × {mult} = <b>{breakdown.outcomePoints}</b> pts
					</div>
					<div className={css(styleSheet.ladder)}>
						{OUTCOME_TIERS.map(tier => {
							const achieved = tier.tier === breakdown.outcomeTier;
							return (
								<div key={tier.tier} className={css(styleSheet.ladderRow, achieved && styleSheet.ladderRowActive)}>
									{achieved ? (
										<CheckIcon color={colors.primary.dark} />
									) : (
										<span className={css(styleSheet.ladderCheckSpacer)} />
									)}
									<span
										className={css(styleSheet.ladderLabel)}
										style={{
											color: achieved ? colors.primary.dark : colors.text.light,
											fontWeight: achieved ? 600 : 400,
										}}
									>
										{tier.label}
									</span>
									<span
										className={css(styleSheet.ladderPoints)}
										style={{ color: achieved ? colors.primary.dark : colors.text.light }}
									>
										{tier.points}
									</span>
								</div>
							);
						})}
					</div>
				</div>

				{negativeModifiers.length > 0 && (
					<div className={css(styleSheet.section)}>
						<div className={css(styleSheet.sectionLabel)}>Negative modifiers</div>
						<div className={css(styleSheet.rowList)}>
							{negativeModifiers.map(id => {
								const meta = NEGATIVE_MODIFIERS[id] ?? { label: id, effect: 'review flag', severe: false };
								return (
									<div key={id} className={css(styleSheet.modRow, meta.severe && styleSheet.modRowSevere)}>
										<span
											className={css(styleSheet.modDot)}
											style={{ backgroundColor: meta.severe ? colors.warning.main : colors.text.light }}
										/>
										<span className={css(styleSheet.modLabel)}>{meta.label}</span>
										<span
											className={css(styleSheet.modTag, meta.severe ? styleSheet.modTagSevere : styleSheet.modTagFlag)}
										>
											{meta.effect}
										</span>
									</div>
								);
							})}
						</div>
					</div>
				)}

				{disqualifiers.length > 0 && (
					<div className={css(styleSheet.section)}>
						<div className={css(styleSheet.sectionLabel)}>Disqualifiers</div>
						<div className={css(styleSheet.rowList)}>
							{disqualifiers.map(id => {
								const meta = DISQUALIFIER_META[id] ?? { id, label: id, tier: 'B' as const, effect: '−3' };
								const tierA = meta.tier === 'A';
								return (
									<div key={id} className={css(styleSheet.dqRow)}>
										<span className={css(styleSheet.dqTier, tierA ? styleSheet.dqTierA : styleSheet.dqTierB)}>
											Tier {meta.tier}
										</span>
										<span className={css(styleSheet.dqLabel)}>{meta.label}</span>
										<span className={css(styleSheet.dqEffect)}>{meta.effect}</span>
									</div>
								);
							})}
						</div>
					</div>
				)}

				{gamePlan && (
					<div className={css(styleSheet.section)}>
						<div className={css(styleSheet.sectionLabel)}>Game plan pitch — not part of the 0–100</div>
						<div className={css(styleSheet.effortBox)}>
							<div>
								<div className={css(styleSheet.effortLabel)}>{gamePlanMeta.label}</div>
								<div className={css(styleSheet.effortSub)}>{gamePlanMeta.sub}</div>
							</div>
							<span className={css(styleSheet.tag, toneStyle(gamePlanMeta.tone))}>{gamePlanMeta.label}</span>
						</div>
						{(gamePlan.reasoning || gamePlan.repEvidence) && (
							<div className={css(styleSheet.whyBox)}>
								{gamePlan.reasoning && <div className={css(styleSheet.whyText)}>{gamePlan.reasoning}</div>}
								{gamePlan.repEvidence && (
									<div className={css(gamePlan.reasoning ? styleSheet.evidenceDivider : undefined)}>
										<div className={css(styleSheet.evidenceLabel)}>Rep said instead</div>
										<div className={css(styleSheet.evidenceText)}>{gamePlan.repEvidence}</div>
									</div>
								)}
							</div>
						)}
					</div>
				)}

				{showSuggestions && (
					<div className={css(styleSheet.section)}>
						<div className={css(styleSheet.sectionLabel)}>AI suggestions — not part of the 0–100</div>
						<div className={css(styleSheet.scorePair)}>
							<div className={css(styleSheet.scoreBox)}>
								<div className={css(styleSheet.scoreBoxLabel)}>Coach quality</div>
								<div className={css(styleSheet.scoreBoxValue)}>
									{result.suggestionQualityScore == null ? '—' : `${result.suggestionQualityScore}%`}
								</div>
								<div className={css(styleSheet.scoreBoxSub)}>Share of suggestions that were sound advice</div>
							</div>
							<div className={css(styleSheet.scoreBox)}>
								<div className={css(styleSheet.scoreBoxLabel)}>Rep adherence</div>
								<div className={css(styleSheet.scoreBoxValue)}>
									{result.suggestionAdherenceScore == null ? '—' : `${result.suggestionAdherenceScore}%`}
								</div>
								<div className={css(styleSheet.scoreBoxSub)}>
									Of the <b>sound</b> advice only — ignoring a bad tip is not a miss
								</div>
							</div>
							<div className={css(styleSheet.scoreBox)}>
								<div className={css(styleSheet.scoreBoxLabel)}>Sound advice missed</div>
								<div className={css(styleSheet.scoreBoxValue)}>{result.missedSoundSuggestions ?? '—'}</div>
								<div className={css(styleSheet.scoreBoxSub)}>The only cell that becomes coaching</div>
							</div>
						</div>

						{/* Not judged is not the same as nothing to see, so say which one it is. */}
						{result.suggestionQualityScore == null && suggestions.length > 0 && (
							<div className={css(styleSheet.note, styleSheet.noteWarning)}>
								These suggestions were never quality-judged, so adherence cannot be scored. Calls coached before the
								quality check shipped all read this way.
							</div>
						)}

						{suggestions.length > 0 && (
							<div className={css(styleSheet.rowList)}>
								{suggestions.map((s, i) => {
									const cls = CLASSIFICATION_META[s.classification] ?? {
										label: s.classification,
										tone: 'neutral' as const,
									};
									const qual = QUALITY_META[s.quality ?? 'Unknown'] ?? QUALITY_META.Unknown;
									const isFinding = s.quality === 'Sound' && s.classification === 'not_followed';
									return (
										<div
											key={`${s.step}-${i}`}
											className={css(styleSheet.suggestionRow, isFinding && styleSheet.suggestionRowMissed)}
										>
											<div className={css(styleSheet.suggestionHead)}>
												<span className={css(styleSheet.suggestionStep)}>{stepLabel(s.step)}</span>
												<span className={css(styleSheet.tag, toneStyle(qual.tone))}>{qual.label}</span>
												<span className={css(styleSheet.tag, toneStyle(cls.tone))}>{cls.label}</span>
											</div>
											{s.reasoning && <div className={css(styleSheet.suggestionReasoning)}>{s.reasoning}</div>}
										</div>
									);
								})}
							</div>
						)}
					</div>
				)}

				<div className={css(styleSheet.section)}>
					<div className={css(styleSheet.effortBox)}>
						<div>
							<div className={css(styleSheet.effortLabel)}>Effort (uncapped)</div>
							<div className={css(styleSheet.effortSub)}>Engagement tiebreaker — not part of the 0–100</div>
						</div>
						<div className={css(styleSheet.effortValue)}>{breakdown.effortRaw}</div>
					</div>
				</div>

				{(breakdown.rationale || breakdown.evidence) && (
					<div className={css(styleSheet.section)}>
						<div className={css(styleSheet.sectionLabel)}>Why</div>
						<div className={css(styleSheet.whyBox)}>
							{breakdown.rationale && <div className={css(styleSheet.whyText)}>{breakdown.rationale}</div>}
							{breakdown.evidence && (
								<div className={css(breakdown.rationale ? styleSheet.evidenceDivider : undefined)}>
									<div className={css(styleSheet.evidenceLabel)}>Evidence</div>
									<div className={css(styleSheet.evidenceText)}>{breakdown.evidence}</div>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
